// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { ProjectModel } from '@project/model/project.model';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { CodingFingerArticleManager } from '../manager/coding-finger-article.manager';
import { ArticleManager } from '@article/manager/article.manager';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Timer } from '@shared/timer';
import { AlternativeReplaceArticleManager } from '../manager/alternative-replace-article.manager';
import { CodingArticleProcessorDelegator } from '../processor/coding-article-processor.delegator';

@Injectable({ providedIn: 'root' })
export class DetachCodingFingerArticleService {

    constructor(
        private projectModel: ProjectModel,
        private codingFingerArticleManager: CodingFingerArticleManager,
        private codingArticleUtils: CodingArticleUtils,
        private articleManager: ArticleManager,
        private codingArticleProcessorDelegator: CodingArticleProcessorDelegator,
        private undoRedoManager: UndoRedoManager,
        private undoRedoHelper: UndoRedoHelper
    ) {}

    /**
     * there are 2 types for this task: first ones the coding finger will just be removed, second ones are done by raplacing the module.
     * second ones are running asynchronously and after and first ones, UndoRedo will be collected as sequence
     *
     * @param {string[]} articleUsageIds
     * @param {boolean} [createUndoOperation=true]
     * @memberof CodingFingerArticleManager
     */
    public async detachCodingFingersFromArticles(alternativeReplaceArticleManager: AlternativeReplaceArticleManager, articleUsageIds: string[], createUndoOperation: boolean = true): Promise<void> {
      const codingFingerUsageIdsToDetach: string[] = [];
      this.codingFingerArticleManager.articleListToReplace = [];
      for (const usageId of articleUsageIds) {
        const articleUsage: ArticleUsageVO = this.projectModel.getArticleUsage(usageId);
        if (articleUsage) {
          const alternativeArticle = this.codingArticleUtils.getDetachCodingfingerAlternativeArticle(articleUsage);
          if (alternativeArticle) {
            this.codingFingerArticleManager.articleListToReplace.push(articleUsage);
          } else {
            let codingFingerUsageId: string = null;
            const articleVO: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
            if (CodingArticleUtils.isCodingFinger(articleVO) || ArticleUtils.isContact(articleVO)) {
              codingFingerUsageId = usageId;
            } else if (!CodingArticleUtils.hasMultipleCodingFingerConnections(articleVO)) {
              codingFingerUsageId = this.codingArticleProcessorDelegator.findCodingFingerUsageIdByParent(articleUsage);
            }
            if (!codingFingerUsageId) {
              continue;
            }
            const codingFingerUsage: ArticleUsageVO = this.projectModel.getArticleUsage(codingFingerUsageId);
            // check that usage exists and is not yet part of the array
            if (codingFingerUsage && codingFingerUsageIdsToDetach.indexOf(codingFingerUsage.id) === -1) {
              codingFingerUsageIdsToDetach.push(codingFingerUsage.id);
            }
          }
        }
      }

      this.undoRedoManager.startUndoOperationSequence();

      const detachOperation: UndoDetachArticlesOperation = await this.undoRedoHelper.forceRemoveArticlesAndReturnUndoOperations(
        codingFingerUsageIdsToDetach);
      if (createUndoOperation && detachOperation) {
        this.undoRedoManager.pushUndo(detachOperation);
      }
      await this.startAsynchronDetachCodingFingerByReplace(alternativeReplaceArticleManager);
    }

  private async startAsynchronDetachCodingFingerByReplace(alternativeReplaceArticleManager: AlternativeReplaceArticleManager): Promise<void> {
    await this.asynchronDetachCodingFingerByReplace(alternativeReplaceArticleManager);
  }

  /**
   * This method will be called recursivly by handler
   *
   * @private
   * @memberof CodingFingerArticleManager
   */
  private async asynchronDetachCodingFingerByReplace(alternativeReplaceArticleManager: AlternativeReplaceArticleManager): Promise<void> {
    if (this.codingFingerArticleManager.articleListToReplace.length > 0 ) {
      const articleUsage = this.codingFingerArticleManager.articleListToReplace.pop();
      await this.codingFingerArticleManager.replaceArticle(alternativeReplaceArticleManager, articleUsage, this.codingArticleUtils.getDetachCodingfingerAlternativeArticle(articleUsage));
    } else {
      this.codingFingerArticleManager.finishAttachDetachOperation();
    }
  }

  public processDetachedCodingFinger(articleContainer: ArticleContainer): void {
    if (!this.codingFingerArticleManager.isCodingFingerHandlingEnabled()) {
      return;
    }
    const parentArticleUsage: ArticleUsageVO = this.codingFingerArticleManager.getBackwardConnectedArticleUsage(articleContainer.articleUsage);
    if (CodingArticleUtils.isCodingFinger(articleContainer.article)) {
      if (parentArticleUsage) {
        this.codingArticleUtils.storeRemovedCodingFingerArticleInformation(parentArticleUsage, articleContainer.articleUsage, true);
        if (!this.codingFingerArticleManager.updateProjectPropertiesTimer.hasEventListener(Timer.TIMER_EVENT)) {
          this.codingFingerArticleManager.updateProjectPropertiesTimer.addEventListener(Timer.TIMER_EVENT, () => {
            this.codingFingerArticleManager.updateProjectPropertiesTimerHandler()
          });
        }
        this.codingFingerArticleManager.queueProjectPropertyUpdate(parentArticleUsage);
      }
    } else if (ArticleUtils.isContact(articleContainer.article)) {
      if (parentArticleUsage) {
        this.codingArticleUtils.storeRemovedContactArticleInformation(parentArticleUsage, articleContainer.articleUsage, true);
        this.codingFingerArticleManager.queueProjectPropertyUpdate(parentArticleUsage);
      }
    } else {
      this.codingFingerArticleManager.updateRemovedCodingFingerPositionsProjectInfoProperty();
    }
  }
}
